import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { productAPI, inventoryAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { Plus, ArrowUp, ArrowDown, AlertTriangle, Edit, FileText } from 'lucide-react';
import { toast } from 'sonner';
import useTable from "../../hooks/UseTable";
import TableTopControls from "../../components/TableTopControls";
import TableBottomControls from "../../components/TableBottomControls";
import useTime from "../../hooks/UseTime";

export default function InventoryAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ product_id: '', quantity_change: '', type: 'restock', notes: '' });
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const { formatDateTime, fromNow } = useTime();

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await productAPI.getAll();
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const qty = parseInt(formData.quantity_change);
      const adjustedQty = formData.type === 'damage' || formData.type === 'loss' ? -Math.abs(qty) : Math.abs(qty);
      await inventoryAPI.update({
        product_id: formData.product_id,
        quantity_change: adjustedQty,
        type: formData.type,
        notes: formData.notes,
      });
      toast.success('Inventory updated successfully');
      setShowForm(false);
      setEditingProduct(null);
      setFormData({ product_id: '', quantity_change: '', type: 'restock', notes: '' });
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update inventory');
    }
  };

  const handleViewLogs = async (product) => {
    setSelectedProduct(product);
    setShowLogsModal(true);
    setLogsLoading(true);

    try {
      const { data } = await inventoryAPI.getLogs(product.id);
      setLogs(data); // no filtering needed
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_id: product.id,
      quantity_change: '',
      type: 'adjustment',
      notes: ''
    });
    setShowForm(true);
  };

  const {
    paginatedData,
    currentPage,
    totalPages,
    search,
    setSearch,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    startItem,
    endItem,
    totalItems
  } = useTable(products, 5, ["name", "category", "franchise_name"]);

  return (
    <AdminLayout activeItem="Inventory">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Inventory</h1>
          {!isSuperAdmin && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
              data-testid="update-inventory-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Update Stock
            </button>
          )}
        </div>
      </header>

      <main className="p-8">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="inventory-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">
                {editingProduct ? `Adjust Stock: ${editingProduct.name}` : 'Update Inventory'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Product</label>
                  <select
                    required
                    value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="inventory-product-input"
                  >
                    <option value="">Select product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (Stock: {p.stock})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="inventory-type-input"
                  >
                    <option value="restock">Restock (Add)</option>
                    <option value="damage">Damage (Remove)</option>
                    <option value="loss">Loss (Remove)</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.quantity_change}
                    onChange={(e) => setFormData({ ...formData, quantity_change: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="inventory-quantity-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    data-testid="inventory-notes-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="inventory-submit-button">
                    Update
                  </button>
                  <button type="button" 
                    onClick={() => {
                      setShowForm(false);
                      setEditingProduct(null);
                      setFormData({ product_id: '', quantity_change: '', type: 'restock', notes: '' });
                    }} 
                    className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showLogsModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowLogsModal(false);
                setSelectedProduct(null);
              }
            }}
          >
            <div className="bg-white p-6 max-w-3xl w-full mx-4 border border-slate-200">
              
              <h2 className="text-xl font-medium text-brand-primary mb-4">
                Inventory Logs - {selectedProduct?.name}
              </h2>

              {logsLoading ? (
                <div className="text-center py-6">Loading...</div>
              ) : logs.length === 0 ? (
                <div className="text-center text-slate-500 py-6">
                  No logs found.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Change</th>
                        <th className="px-4 py-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-t">
                          <td>
                            {formatDateTime(log.created_at)}
                          </td>
                          <td className="px-4 py-2 capitalize">{log.type}</td>
                          <td
                            className={`px-4 py-2 font-medium ${
                              log.quantity_change > 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {log.quantity_change > 0 ? "+" : ""}
                            {log.quantity_change}
                          </td>
                          <td className="px-4 py-2">
                            {log.notes || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setShowLogsModal(false);
                    setSelectedProduct(null);
                  }}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <TableTopControls
              search={search}
              setSearch={setSearch}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="inventory-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">
                        Franchise
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Product</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Current Stock</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Status</th>
                    {!isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-4 text-center text-slate-500">No products found.</td></tr>
                  ) : (
                    paginatedData.map((product) => (
                    <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50">
                      {isSuperAdmin && (
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {product.franchise_name || '-'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-slate-800">{product.name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <span className="px-3 py-1 bg-brand-cyan-light text-brand-primary text-xs font-medium">
                          {product.category.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-brand-primary">{product.stock}</td>
                      <td className="px-6 py-4 text-sm">
                        {product.stock <= 0 ? (
                          <span className="flex items-center text-red-600">
                            <AlertTriangle className="h-4 w-4 mr-1" /> Out of Stock
                          </span>
                        ) : product.stock < 10 ? (
                          <span className="flex items-center text-yellow-600">
                            <ArrowDown className="h-4 w-4 mr-1" /> Low Stock
                          </span>
                        ) : (
                          <span className="flex items-center text-green-600">
                            <ArrowUp className="h-4 w-4 mr-1" /> In Stock
                          </span>
                        )}
                      </td>
                      {!isSuperAdmin && (
                        <td className="px-6 py-4 text-sm">
                          <button onClick={() => handleEdit(product)}
                            className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                          >
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleViewLogs(product)}
                            className="pl-3 inline-flex items-center py-1 text-sm text-brand-primary hover:text-brand-accent transition-colors duration-200"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            View Logs
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>
            <TableBottomControls
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              startItem={startItem}
              endItem={endItem}
              totalItems={totalItems}
            />
          </div>
        )}
      </main>
    </AdminLayout>
  );
}
